import { createApi } from '@reduxjs/toolkit/query/react';

import { VIOLATIONS_TAB_LIST_AD_FILTER } from '../../../shared/constants';
import { axiosBaseQuery } from '../../../shared/services/baseQuery';
import { getUniqueLabelEntries, type Primitive } from '../utils';

import {
  type FiltersGeneralProps,
  type GetAccountsApiResponse,
  type GetApiResponse,
  type GetApplicationEndpointApiResponse,
  type GetBusinessProcessApiResponse,
  type GetColumnPickerApiResponse,
  type GetEntitlementsApiResponse,
  type GetEntitlementTypessApiResponse,
  type GetFunctionAnalysisResponse,
  type GetFunctionsApiResponse,
  type GetInsightDetailsApiResponse,
  type GetMitiatingControlListProps,
  type GetMitigatingControlsApiResponse,
  type GetRecommendedMitiatingControlListProps,
  type GetRiskApiResponse,
  type GetRulesetsApiResponse,
  type GetTabCountApiResponse,
  type GetUserFiltersApiResponse,
  type GetViolationActionResponse,
  type GetViolationsGraphClusterResponse,
  type GetViolationsGraphResponse,
  type GetWidgetsResponse,
  type SodConsultRiskProps,
  type SodExtendReviewEndDateRiskProps,
  type SodGenerateSyncReportProps,
  type SodMitigateProps,
  type SodMitigatingControlsEndDateProps,
  type SodRemediateRiskProps,
  type SodRemoveMitigatingControlProps,
  type SodReOpenRiskProps,
  type SodViolationsListProps,
  ViolationsTabType,
} from './violationsTabTypes';

export const violationsTabApi = createApi({
  reducerPath: ViolationsTabType.API_SLICE_NAME,
  baseQuery: axiosBaseQuery({}),
  endpoints: () => ({}),
});

export const violationsTabApiEndpoints = violationsTabApi.injectEndpoints({
  endpoints: (builder) => ({
    // Tabs
    getTabCount: builder.query<unknown, void>({
      query: () => ({
        url: '/api/sodms/sod-violations/status/counts',
        isECMv6: true,
      }),
      transformResponse: (response: { data?: GetTabCountApiResponse }) =>
        response.data ?? undefined,
    }),
    // Columns
    getColumnPicker: builder.query<unknown, string>({
      query: (view) => ({
        url: '/api/sodms/view/columns',
        params: {
          view,
        },
        isECMv6: true,
      }),
      transformResponse: (response: GetColumnPickerApiResponse) => {
        if (response.status === 200) {
          return response.data;
        }

        return [];
      },
    }),
    // Filters
    getRisks: builder.mutation<unknown, FiltersGeneralProps>({
      query: (payload) => ({
        url: '/api/sodms/risks',
        method: 'POST',
        data: {
          includeDetails: true,
          riskName: `${payload.search ?? ''}`,
          max: payload.max,
          offset: payload.offset,
        },
        isECMv6: true,
      }),
      transformResponse: (response: GetRiskApiResponse) => {
        if (
          response.status === 200 &&
          response.displayRecords &&
          response.displayRecords > 0
        ) {
          const uniqueValues = getUniqueLabelEntries(
            response.data as Record<string, Primitive>[],
            'riskName'
          );

          return uniqueValues;
        }

        return [];
      },
    }),
    getBusinessProcess: builder.query<unknown, FiltersGeneralProps>({
      query: (payload) => ({
        url: '/api/sodms/business-process',
        params: {
          includeDetails: 'true',
          max: payload.max,
          offset: payload.offset,
          businessProcess: `${payload.search ?? ''}`,
        },
        isECMv6: true,
      }),
      transformResponse: (response: GetBusinessProcessApiResponse) => {
        if (
          response.status === 200 &&
          response.displayRecords &&
          response.displayRecords > 0
        ) {
          const uniqueValues = getUniqueLabelEntries(
            response.data as Record<string, Primitive>[],
            'businessProcess'
          );

          return uniqueValues;
        }

        return [];
      },
    }),
    getFunctions: builder.query<unknown, FiltersGeneralProps>({
      query: (payload) => ({
        url: '/api/sodms/functions',
        params: {
          includeDetails: 'true',
          max: payload.max,
          offset: payload.offset,
          functionName: `${payload.search ?? ''}`,
        },
        isECMv6: true,
      }),
      transformResponse: (response: GetFunctionsApiResponse) => {
        if (
          response.status === 200 &&
          response.displayRecords &&
          response.displayRecords > 0
        ) {
          const uniqueValues = getUniqueLabelEntries(
            response.data as Record<string, Primitive>[],
            'functionName'
          );

          return uniqueValues.map((item, index) => ({
            ...item,
            id: response.data?.[index]?.id,
          }));
        }

        return [];
      },
    }),
    getRulesets: builder.query<unknown, FiltersGeneralProps>({
      query: (payload) => ({
        url: '/api/sodms/rulesets',
        params: {
          includeDetails: 'true',
          max: payload.max,
          offset: payload.offset,
          rulesetName: `${payload.search ?? ''}`,
        },
        isECMv6: true,
      }),
      transformResponse: (response: GetRulesetsApiResponse) => {
        if (
          response.status === 200 &&
          response.displayRecords &&
          response.displayRecords > 0
        ) {
          return response.data?.map((ruleset) => ({
            label: `${ruleset.rulesetName}`,
            value: `${ruleset.id}`,
            isChecked: false,
          }));
        }

        return [];
      },
    }),
    getApplicationEndpoints: builder.mutation<unknown, FiltersGeneralProps>({
      query: (payload) => ({
        url: '/api/v5/getEndpoints',
        method: 'POST',
        params: {
          max: payload.max,
          offset: payload.offset,
          endpointname:
            `${payload.search ?? ''}`.trim().length > 0
              ? `${payload.search ?? ''}`
              : undefined,
        },
      }),
      transformResponse: (response: GetApplicationEndpointApiResponse) => {
        if (
          response.errorCode === '0' &&
          response.displayCount &&
          response.displayCount > 0
        ) {
          const uniqueValues = getUniqueLabelEntries(
            response.endpoints as Record<string, Primitive>[],
            'displayName'
          );

          return uniqueValues;
        }

        return [];
      },
    }),
    getUsers: builder.mutation<unknown, FiltersGeneralProps>({
      query: (payload) => ({
        url: '/api/v5/getUser',
        method: 'POST',
        data: {
          filtercriteria: {
            [payload.key ?? 'username']: `*${payload.search ?? ''}*`,
          },
          showsecurityanswers: '1',
        },
        params: {
          max: payload.max,
          offset: payload.offset,
        },
      }),
      transformResponse: (response: GetUserFiltersApiResponse, _, payload) => {
        if (
          response.errorCode === '0' &&
          response.displaycount &&
          response.displaycount > 0
        ) {
          type UserListItem = NonNullable<
            GetUserFiltersApiResponse['userlist']
          >[number];

          const isForUniqueValues = Boolean(
            payload.key === VIOLATIONS_TAB_LIST_AD_FILTER.COSTCENTER.value ||
              payload.key === VIOLATIONS_TAB_LIST_AD_FILTER.FIRSTNAME.value ||
              payload.key === VIOLATIONS_TAB_LIST_AD_FILTER.LASTNAME.value ||
              payload.key === VIOLATIONS_TAB_LIST_AD_FILTER.USERNAME.value ||
              payload.key === VIOLATIONS_TAB_LIST_AD_FILTER.EMAIL.value ||
              payload.key === VIOLATIONS_TAB_LIST_AD_FILTER.DISPLAYNAME.value ||
              payload.key === VIOLATIONS_TAB_LIST_AD_FILTER.DEPARTMENTNAME.value
          );

          if (isForUniqueValues) {
            const uniqueValues = getUniqueLabelEntries(
              response.userlist as Record<string, Primitive>[],
              payload.key ?? 'username'
            );

            return uniqueValues;
          }

          return response.userlist?.map((user) => ({
            label: user[(payload.key ?? 'username') as keyof UserListItem],
            value: `${user.userKey}`,
            isChecked: false,
          }));
        }

        return [];
      },
    }),
    getAccounts: builder.mutation<unknown, FiltersGeneralProps>({
      query: (payload) => ({
        url: '/api/v5/getAccounts',
        method: 'POST',
        params: { max: payload.max, offset: payload.offset },
        data: {
          accountQuery: "acc.status='1'",
          advsearchcriteria: {
            status: 'ACTIVE',
            [payload.key ?? 'name']: `*${payload.search ?? ''}*`,
          },
        },
      }),
      transformResponse: (response: GetAccountsApiResponse, _, payload) => {
        if (
          response.errorCode === '0' &&
          response.displaycount &&
          response.displaycount > 0
        ) {
          type AccountListItem = NonNullable<
            GetAccountsApiResponse['Accountdetails']
          >[number];

          const isForUniqueValues = Boolean(
            payload.key === VIOLATIONS_TAB_LIST_AD_FILTER.NAME.value
          );

          if (isForUniqueValues) {
            const uniqueValues = getUniqueLabelEntries(
              response.Accountdetails as Record<string, Primitive>[],
              payload.key ?? 'name'
            );

            return uniqueValues;
          }

          return response.Accountdetails?.map((account) => ({
            label: account[(payload.key ?? 'name') as keyof AccountListItem],
            value: `${account.accountKey}`,
            isChecked: false,
          }));
        }

        return [];
      },
    }),
    getEntitlements: builder.mutation<unknown, FiltersGeneralProps>({
      query: (payload) => ({
        url: '/api/v5/getEntitlements',
        method: 'POST',
        params: { max: payload.max, offset: payload.offset },
        data: {
          entQuery: `ent.entitlement_value like '%${payload.search ?? ''}%'`,
        },
      }),
      transformResponse: (response: GetEntitlementsApiResponse) => {
        if (
          response.errorCode === '0' &&
          response.entitlementsCount &&
          response.entitlementsCount > 0
        ) {
          const uniqueValues = getUniqueLabelEntries(
            response.Entitlementdetails as Record<string, Primitive>[],
            'entitlement_value'
          );

          return uniqueValues;
        }

        return [];
      },
    }),
    getEntitlementTypes: builder.query<unknown, FiltersGeneralProps>({
      query: (payload) => ({
        url: '/api/v5/getEntitlementTypes',
        params: {
          max: payload.max,
          offset: payload.offset,
          entitlementname: `${payload.search ?? ''}`,
        },
      }),
      transformResponse: (response: GetEntitlementTypessApiResponse) => {
        if (
          response.errorCode === '0' &&
          response.displayCount &&
          response.displayCount > 0
        ) {
          const uniqueValues = getUniqueLabelEntries(
            response.entitlementTypeDetails as Record<string, Primitive>[],
            'displayName'
          );

          return uniqueValues;
        }

        return [];
      },
    }),
    // List
    getMitiatingControls: builder.query<unknown, FiltersGeneralProps>({
      query: (payload) => ({
        url: '/api/sodms/mitigating-control',
        params: {
          includeDetails: 'false',
          mitigatingControl: `${payload.search ?? ''}`,
        },
        isECMv6: true,
      }),
      transformResponse: (response: GetMitigatingControlsApiResponse) => {
        if (
          response.status === 200 &&
          response.displayRecords &&
          response.displayRecords > 0
        ) {
          return response.data?.map((mitigatingControl) => ({
            label: `${mitigatingControl.mitigatingControl}`,
            value: `${mitigatingControl.mitigatingControlId}`,
            isChecked: false,
          }));
        }

        return [];
      },
    }),
    getViolationsList: builder.mutation<unknown, SodViolationsListProps>({
      query: (payload) => ({
        url: '/api/sodms/sod-violations/search',
        method: 'POST',
        data: payload,
        isECMv6: true,
      }),
    }),
    getInsightsDetails: builder.query<
      unknown,
      Omit<SodViolationsListProps, 'pagination'>
    >({
      query: (payload) => ({
        url: '/api/sodms/sod-violations/insights/search',
        isECMv6: true,
        method: 'POST',
        params: {
          view: 'list',
        },
        data: payload,
      }),
      transformResponse: (response: GetInsightDetailsApiResponse) => {
        if (response.data) {
          const finalResponse = response.data;

          return finalResponse;
        }

        return [];
      },
    }),
    // Dashboard
    getViolationsGraph: builder.mutation<
      unknown,
      Pick<SodViolationsListProps, 'filters'>
    >({
      query: (payload) => ({
        url: '/api/sodms/sod-violations/graph',
        isECMv6: true,
        method: 'POST',
        data: payload,
      }),
      transformResponse: (response: GetViolationsGraphResponse) => {
        if (response.status === 200) {
          return (
            response.data ?? {
              totalViolations: 0,
              clusters: [],
              maxViolationAge: 55,
              maxViolationScore: 80,
              minViolationAge: 25,
              minViolationScore: 0,
            }
          );
        }

        return {};
      },
    }),
    getViolationsGraphCuster: builder.mutation<
      unknown,
      {
        clusterId: string;
        payload: Pick<SodViolationsListProps, 'filters' | 'pagination'>;
      }
    >({
      query: (payload) => ({
        url: `/api/sodms/sod-violations/graph/cluster/${payload.clusterId}`,
        isECMv6: true,
        method: 'POST',
        data: payload.payload,
      }),
      transformResponse: (response: GetViolationsGraphClusterResponse) => {
        if (
          response.status === 200 &&
          response.displayRecords &&
          response.displayRecords > 0
        ) {
          return {
            data: response.data,
            page: response.page,
            totalRecords: response.totalRecords,
          };
        }

        return {};
      },
    }),
    getWidgets: builder.mutation<
      unknown,
      {
        endpoint: string;
        payload: Pick<SodViolationsListProps, 'filters'>;
      }
    >({
      query: (payload) => ({
        url: `/api/sodms${payload.endpoint}`,
        isECMv6: true,
        method: 'POST',
        data: payload.payload,
      }),
      transformResponse: (response: GetWidgetsResponse) => {
        if (
          response.status === 200 &&
          response.displayRecords &&
          response.displayRecords > 0
        ) {
          return response.data;
        }

        return {};
      },
    }),
    // Actions starting from here
    reOpenRisk: builder.mutation<unknown, SodReOpenRiskProps>({
      query: (payload) => ({
        url: '/api/sodms/sod-violations/bulk/open',
        isECMv6: true,
        method: 'PUT',
        data: payload,
      }),
      transformResponse: (response: GetViolationActionResponse) => {
        if (response.status === 200) {
          return { count: response.data.count, message: response.message };
        }

        throw Error(response.message);
      },
    }),
    consultRisk: builder.mutation<unknown, SodConsultRiskProps>({
      query: (payload) => ({
        url: '/api/sodms/sod-violations/bulk/consult',
        isECMv6: true,
        method: 'PUT',
        data: payload,
      }),
      transformResponse: (response: GetViolationActionResponse) => {
        if (response.status === 200) {
          return { count: response.data.count, message: response.message };
        }

        throw Error(response.message);
      },
    }),
    extendReviewEndDate: builder.mutation<
      unknown,
      SodExtendReviewEndDateRiskProps
    >({
      query: (payload) => ({
        url: '/api/sodms/sod-violations/actions/bulk/violations/in-review/extend-end-dates',
        isECMv6: true,
        method: 'PUT',
        data: payload,
      }),
      transformResponse: (response: GetViolationActionResponse) => {
        if (response.status === 200) {
          return { count: response.data.count, message: response.message };
        }

        throw Error(response.message);
      },
    }),
    getRemediateOptions: builder.query<unknown, void>({
      query: () => ({
        url: '/api/idw/config/REMEDIATE_OPTIONS',
        isECMv6: true,
      }),
    }),
    getFunctionAnalysis: builder.query<unknown, string>({
      query: (violationId) => ({
        url: `/api/sodms/sod-violations/${violationId}/remediate/function-analysis`,
        isECMv6: true,
      }),
      transformResponse: (response: GetFunctionAnalysisResponse) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (response.status === 200 && response.data) {
          return { data: response.data };
        }

        throw Error(response.message);
      },
    }),
    remediateRisk: builder.mutation<unknown, SodRemediateRiskProps>({
      query: ({ violationId, payload }) => ({
        url: `/api/sodms/sod-violations/${violationId}/remediate`,
        isECMv6: true,
        method: 'PUT',
        data: payload,
      }),
      transformResponse: (response: GetViolationActionResponse) => {
        if (response.status === 200) {
          return { count: 1, message: response.message };
        }

        throw Error(response.message);
      },
    }),
    removeMitigatingControl: builder.mutation<
      unknown,
      SodRemoveMitigatingControlProps
    >({
      query: (payload) => ({
        url: `/api/sodms/sod-violations/actions/mitigating-control/remove`,
        isECMv6: true,
        method: 'PUT',
        data: payload,
      }),
      transformResponse: (response: GetViolationActionResponse) => {
        if (response.status === 200) {
          return { count: 1, message: response.message };
        }

        throw Error(response.message);
      },
    }),
    mitigateControlsEndDate: builder.mutation<
      unknown,
      SodMitigatingControlsEndDateProps
    >({
      query: (payload) => ({
        url: '/api/sodms/sod-violations/bulk/violations/mitigating-controls/end-date',
        isECMv6: true,
        method: 'PUT',
        data: payload,
      }),
      transformResponse: (response: GetViolationActionResponse) => {
        if (response.status === 200) {
          return { count: response.data.count, message: response.message };
        }

        throw Error(response.message);
      },
    }),
    generateSyncReport: builder.mutation<unknown, SodGenerateSyncReportProps>({
      query: (payload) => ({
        url: '/api/sodms/sod-violations/reports/exports/sync',
        isECMv6: true,
        method: 'POST',
        data: payload,
        config: { responseType: 'blob' },
      }),
      transformResponse: (response: unknown) => response as Blob,
      transformErrorResponse: (error: GetApiResponse) => {
        throw Error(error.message);
      },
    }),
    getMitiatingControlList: builder.query<
      unknown,
      GetMitiatingControlListProps
    >({
      query: (parmas) => ({
        url: '/api/sodms/mitigating-control',
        params: { ...parmas },
        isECMv6: true,
      }),
      transformResponse: (response: GetMitigatingControlsApiResponse) => {
        if (response.status === 200 && response.displayRecords !== undefined) {
          return { totalRecords: response.totalRecords, data: response.data };
        }

        throw Error(response.message);
      },
    }),
    getRecommendedMitiatingControlList: builder.query<
      unknown,
      GetRecommendedMitiatingControlListProps
    >({
      query: (payload) => ({
        url: '/api/sodms/risks/mitigating-controls',
        isECMv6: true,
        params: {
          riskId: payload.riskId,
          offset: payload.offset,
          max: payload.max,
          order: payload.order,
          columnToSort: payload.columnToSort,
          searchText: payload.searchText,
          recommended: payload.recommended,
        },
      }),
      transformResponse: (response: GetViolationActionResponse) => {
        if (response.status === 200 && response.displayRecords !== undefined) {
          return { totalRecords: response.totalRecords, data: response.data };
        }

        throw Error(response.message);
      },
    }),
    mitigate: builder.mutation<unknown, SodMitigateProps>({
      query: (payload) => ({
        url: '/api/sodms/sod-violations/bulk/mitigate',
        isECMv6: true,
        method: 'PUT',
        data: payload,
      }),
      transformResponse: (response: GetViolationActionResponse) => {
        if (response.status === 200) {
          return { message: response.message };
        }

        throw Error(response.message);
      },
    }),
    addMitigatingControl: builder.mutation<unknown, SodMitigateProps>({
      query: (payload) => ({
        url: '/api/sodms/sod-violations/bulk/mitigate/append',
        isECMv6: true,
        method: 'PUT',
        data: payload,
      }),
      transformResponse: (response: GetViolationActionResponse) => {
        if (response.status === 200) {
          return { message: response.message };
        }

        throw Error(response.message);
      },
    }),
  }),
});

export const {
  useLazyGetTabCountQuery,
  useLazyGetColumnPickerQuery,
  useGetRisksMutation,
  useLazyGetBusinessProcessQuery,
  useLazyGetFunctionsQuery,
  useLazyGetRulesetsQuery,
  useGetApplicationEndpointsMutation,
  useGetUsersMutation,
  useGetAccountsMutation,
  useGetEntitlementsMutation,
  useLazyGetEntitlementTypesQuery,
  useLazyGetMitiatingControlsQuery,
  useGetViolationsListMutation,
  useGetInsightsDetailsQuery,
  useLazyGetInsightsDetailsQuery,
  useGetViolationsGraphMutation,
  useGetViolationsGraphCusterMutation,
  useGetWidgetsMutation,
  // Actions
  useReOpenRiskMutation,
  useConsultRiskMutation,
  useExtendReviewEndDateMutation,
  useLazyGetRemediateOptionsQuery,
  useLazyGetFunctionAnalysisQuery,
  useRemediateRiskMutation,
  useRemoveMitigatingControlMutation,
  useMitigateControlsEndDateMutation,
  useGenerateSyncReportMutation,
  useLazyGetMitiatingControlListQuery,
  useLazyGetRecommendedMitiatingControlListQuery,
  useMitigateMutation,
  useAddMitigatingControlMutation,
} = violationsTabApiEndpoints;
